import axios from 'axios';
import { v4 as uuid } from 'uuid';
import lscache from 'lscache';

import webNodeHelper from './webnode-helper';

class Helpers {
	constructor() {
		this.analyticsProps = {};
	}
	// ***************************
	// Analytics
	// ***************************
	async initFormAnalytics({ formName, analyticsProps = {} }) {
		this._satellite = window._satellite;
		if (typeof this._satellite === 'undefined' && window.top) {
			try {
				this._satellite = window.top._satellite;
				window.digitalData = window.top.digitalData;
				window.dataLayer = window.top.dataLayer;
			} catch (err) {
				console.error(err);
			}
		}
		if (typeof this._satellite === 'undefined') {
			const isPrd = ['www.esri.com', 'assets.esri.com'].indexOf(window.location.name) > -1;
			const src = isPrd
				? 'https://assets.adobedtm.com/launch-EN26af1ee4ef084984926fd80d1cb93835.min.js'
				: 'https://assets.adobedtm.com/launch-EN586f16b68a3d4bb3a12d070914d0502f-staging.min.js';
			await this.createScriptTag(src);
			this._satellite = window._satellite;
		}

		const cachedProps = lscache.get('esridataLayerInfo');

		this.formName = formName;
		let originatorUrl = '';
		try {
			originatorUrl = window.top.location.href.split('?')[0].split('#')[0];
		} catch (err) {
			originatorUrl = window.location.href.split('?')[0].split('#')[0];
		}
		this.formId = `${formName} - ${originatorUrl}`;
		this.submissionID = cachedProps?.submissionId || uuid();

		const clientIp = await this.getClientIP();

		this.analyticsProps = {
			formName: this.formName,
			formID: this.formId,
			submissionID: this.submissionID,
			clientIp,
			originatorURL: originatorUrl,
			sfid: this.getSFId(),
			...analyticsProps,
		};

		if (this._satellite && !cachedProps) {
			this._satellite.track('formLoad', this.analyticsProps);
		}
		webNodeHelper.recordFormLoad(this.analyticsProps);

		return this.analyticsProps;
	}

	async trackFormStart({ analyticsProps = {} }) {
		this.analyticsProps = {
			...this.analyticsProps,
			...analyticsProps,
		};
		if (this._satellite) {
			this._satellite.track('formStart', this.analyticsProps);
		}
		webNodeHelper.recordFormStart(this.analyticsProps);
		return this.analyticsProps;
	}

	async trackFormSubmit({ isMQL = false, analyticsProps = {} }) {
		if (this._satellite) {
			this.analyticsProps = {
				...this.analyticsProps,
				...analyticsProps,
				isMQL: isMQL,
			};

			this._satellite.track('formSubmit', this.analyticsProps);
			webNodeHelper.recordFormSubmit(this.analyticsProps);
			//Sets form info to be read by the thankyou page
			lscache.set(
				'esridataLayerInfo',
				{
					formName: this.analyticsProps.formName,
					formID: this.analyticsProps.formID,
					submissionId: this.analyticsProps.submissionID,
					originatorURL: this.analyticsProps.originatorURL,
					isMql: this.analyticsProps.isMQL,
				},
				30
			);

			const visitorId = this._satellite.getVisitorId();
			const marketingCloudVisitorID = visitorId ? visitorId.getMarketingCloudVisitorID() : null;
			this.analyticsProps.marketingCloudVisitorID = marketingCloudVisitorID;

			return this.analyticsProps;
		}
	}

	// ***************************
	// SFID
	// ***************************
	getSFId() {
		/*  let searchString = '';
        try {
            searchString = window.top.location.search;
        } catch (err) {
            searchString = window.top.location.search;
        }

        let { sf_id } = queryString.parse(searchString.toLowerCase());
        if (!sf_id) {
            sf_id = Cookies.get('sf_id');
        }
        return sf_id; */
		return '';
	}

	// ***************************
	// GeoIP
	// ***************************
	async initGeoIP() {
		if (typeof window.geoip2 === 'undefined') {
			await this.createScriptTag('https://geoip.esri.com/geoip.js');
			if (typeof window.geoip2 === 'undefined') {
				throw new Error('Geo IP Service Could not be loaded');
			}
		}
		return window.geoip2;
	}

	async getGeoIPInfo() {
		await this.initGeoIP();
		return new Promise((resolve) => {
			if (typeof window.geoip2 === 'undefined') {
				resolve();
			} else {
				try {
					window.geoip2.insights(
						(geoInfo) => {
							resolve(geoInfo);
						},
						(err) => {
							resolve();
						}
					);
				} catch (e) {
					resolve();
				}
			}
		});
	}

	async getClientIP() {
		const geoInfo = await this.getGeoIPInfo();
		if (geoInfo) {
			const clientIP = geoInfo.traits.ip_address;
			return clientIP;
		} else {
			return '1.1.1.1';
		}
	}

	async getVisitorCountry() {
		const geoInfo = await this.getGeoIPInfo();
		if (geoInfo) {
			const country = geoInfo?.country?.names?.en || 'United States';
			return country;
		} else {
			return 'United States';
		}
	}

	async getVisitorCountryCode() {
		const geoInfo = await this.getGeoIPInfo();
		if (geoInfo) {
			const countryCode = geoInfo?.country?.iso_code || 'US';
			return countryCode;
		} else {
			return 'US';
		}
	}

	async checkIfInEU(countryOverride) {
		return new Promise(async (resolve) => {
			try {
				if (countryOverride) {
					const results = await axios.get(`https://geoip.esri.com/get-in-eu?name=${countryOverride}`);
					if (results.data && results.data.length > 0) {
						const isInEU = results.data[0].is_in_european_union === '1';
						resolve(isInEU);
					}
				} else {
					const geoInfo = await this.getGeoIPInfo();
					if (geoInfo) {
						const isInEU = geoInfo?.country?.is_in_european_union;
						resolve(isInEU);
					} else {
						resolve(true);
					}
				}
			} catch (e) {
				resolve(true);
			}
		});
	}

	// ***************************
	// Standard Value Lookups
	// ***************************
	async loadAemFormData(formPath, controlId = 'guidedropdownlist') {
		const proxy = 'https://webnode.esri.com/3014/proxy?url=';
		const aemFormsRoot = 'https://aem-prd.esri.com/content/forms/af';
		const url = `${proxy}${aemFormsRoot}${formPath}.5.json`;

		const res = await fetch(url);
		const data = await res.json();

		const optionPairs = data['jcr:content']?.guideContainer?.rootPanel?.items?.[controlId]?.options;

		const options = optionPairs.map((opt) => {
			const optPair = opt.split('=');
			return {
				display: optPair[1],
				value: optPair[0],
			};
		});
		return options;
	}

	// ***************************
	// Should Component Show Error
	// ***************************
	shouldComponentShowError({ showError, formHasHadAnInValidSubmit, required, getErrorMessage, errorMessage, currentValue }) {
		const response = {
			showError: false,
			errorMessage: '',
		};

		if (required && formHasHadAnInValidSubmit && !currentValue) {
			response.showError = true;
		} else {
			if (typeof showError === 'function') {
				response.showError = showError();
			} else {
				response.showError = showError;
			}
		}

		if (response.showError) {
			if (errorMessage) {
				response.errorMessage = errorMessage;
			} else if (getErrorMessage && typeof getErrorMessage === 'function') {
				response.errorMessage = getErrorMessage();
			}
			if (!response.errorMessage) {
				response.errorMessage = required ? 'This field is is required.' : 'there was an error validating this field';
			}
		}

		return response;
	}

	// ***************************
	// Email Validation
	// ***************************
	async validateEmailWithStrikeiron(email) {
		const payload = encodeURIComponent(JSON.stringify({ email: email }));
		const formBody = [`payload=${payload}`];
		const res = await fetch('https://webnode.esri.com/3014/email_validation_service/email-validation/', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
			},
			body: formBody,
		});
		const data = await res.json();

		return data.EmailStatusCode && data.EmailStatusCode === '1000';
	}

	// ***************************
	// Language Property
	// ***************************
	matchLang(langChoice, langOpt) {
		const langChoiceLower = langChoice.toLowerCase();
		const langOptLower = langOpt.toLowerCase();

		if (langChoiceLower === langOptLower) {
			return true;
		} else if (langChoiceLower.indexOf('-') > -1) {
			const langBase = langChoiceLower.split('-')[0];
			// en-au matches en

			if (langBase === langOptLower) {
				return true;
			}
			// fr-ca matches fr-fr
			if (`${langBase}-${langBase}` === langOptLower) {
				return true;
			}
		} else if (langOptLower.indexOf('-') > -1) {
			// de matches de-de
			if (`${langChoiceLower}-${langChoiceLower}` === langOptLower) {
				return true;
			}
			if (langOptLower.indexOf('-') > -1) {
				// zh matches Zh-cn
				const langChoiceBase = langOptLower.split('-')[0];

				if (langChoiceLower === langChoiceBase) {
					return true;
				}
			}
		}

		return false;
	}

	getPageLanguage() {
		const pageLang = window.document.documentElement.lang || 'en';

		const languages = [
			{ code: 'ar-SA', language: 'Arabic' },
			{ code: 'bs-BA', language: 'Bosnian' },
			{ code: 'ca-es', language: 'Catalan' },
			{ code: 'hr-HR', language: 'Croatian' },
			{ code: 'cs-CZ', language: 'Czech' },
			{ code: 'da-DK', language: 'Danish' },
			{ code: 'nl-NL', language: 'Dutch' },
			{ code: 'en', language: 'English' },
			{ code: 'et-EE', language: 'Estonian' },
			{ code: 'fi-FI', language: 'Finnish' },
			{ code: 'Fr-fr', language: 'French' },
			{ code: 'Fr-ch', language: 'French' },
			{ code: 'De-de', language: 'German' },
			{ code: 'De-ch', language: 'German' },
			{ code: 'el-GR', language: 'Greek' },
			{ code: 'he-IL', language: 'Hebrew' },
			{ code: 'hi-IN', language: 'Hindi' },
			{ code: 'hu-HU', language: 'Hungarian' },
			{ code: 'is', language: 'Icelandic' },
			{ code: 'id-ID', language: 'Indonesian' },
			{ code: 'It-it', language: 'Italian' },
			{ code: 'ja-JP', language: 'Japanese' },
			{ code: 'ko-KR', language: 'Korean' },
			{ code: 'lv-LV', language: 'Latvian' },
			{ code: 'lt-LT', language: 'Lithuanian' },
			{ code: 'mn', language: 'Mongolian' },
			{ code: 'no-NO', language: 'Norwegian' },
			{ code: 'nb-NO', language: 'Norwegian' },
			{ code: 'pl-PL', language: 'Polish' },
			{ code: 'Pt-br', language: 'Portuguese (Brazil)' },
			{ code: 'Pt-pt', language: 'Portuguese (Portugal)' },
			{ code: 'ro-RO', language: 'Romanian' },
			{ code: 'ru-RU', language: 'Russian' },
			{ code: 'sr-RS', language: 'Serbian' },
			{ code: 'Zh-cn', language: 'Simplified Chinese' },
			{ code: 'sl-sl', language: 'Slovenian' },
			{ code: 'es-Es', language: 'Spanish' },
			{ code: 'sv-SE', language: 'Swedish' },
			{ code: 'th-TH', language: 'Thai' },
			{ code: 'Zh-hk', language: 'Traditional Chinese (Hong Kong)' },
			{ code: 'Zh-tw', language: 'Traditional Chinese (Taiwan)' },
			{ code: 'tr-TR', language: 'Turkish' },
			{ code: 'uk-UA', language: 'Ukranian' },
			{ code: 'vi-VN', language: 'Vietnamese' },
		];

		const languageLookup = languages.filter((entry) => this.matchLang(pageLang, entry.code))[0];
		return languageLookup ? languageLookup.language : 'English';
	}

	// ***************************
	// Utility
	// ***************************
	createScriptTag(src) {
		return new Promise((resolve) => {
			const $scriptTag = document.createElement('script');

			$scriptTag.src = src;
			$scriptTag.onload = resolve;
			document.body.appendChild($scriptTag);
		});
	}

	createIframe(url) {
		return new Promise((resolve) => {
			const $iframe = document.createElement('iframe');

			$iframe.setAttribute('src', url);
			$iframe.style.display = 'none';
			$iframe.onload = () => {
				resolve();
			};
			document.body.appendChild($iframe);
			setTimeout(() => {
				resolve();
			}, 1000);
		});
	}
}

export default new Helpers();
